import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { GOLD_PACKS, formatEuro, formatGold } from "@/lib/payments/gold-packs";
import { LegalFooter } from "@/components/LegalFooter";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Xenomons – Pricing & Gold Packs" },
      {
        name: "description",
        content:
          "All Gold packs for Xenomons. Prices in EUR, sold by Schmetti GmbH. Payments processed by Paddle as Merchant of Record.",
      },
      { property: "og:title", content: "Xenomons – Pricing & Gold Packs" },
      {
        property: "og:description",
        content: "All Gold packs and prices for Xenomons at a glance.",
      },
      { property: "og:url", content: "https://xenomons.online/pricing" },
    ],
    links: [
      { rel: "canonical", href: "https://xenomons.online/pricing" },
      { rel: "alternate", hrefLang: "de", href: "https://xenomons.online/pricing" },
      { rel: "alternate", hrefLang: "en", href: "https://xenomons.online/pricing" },
      { rel: "alternate", hrefLang: "x-default", href: "https://xenomons.online/pricing" },
    ],
  }),
  component: PricingPage,
});

function PricingPage() {
  const { t } = useTranslation("legal");

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-30" aria-hidden />
      <div className="absolute inset-0 scanlines pointer-events-none opacity-40" aria-hidden />

      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-md bg-gradient-to-br from-primary to-accent shadow-glow" />
          <span className="font-display text-xl font-bold tracking-widest text-glow">
            XENOMONS
          </span>
        </Link>
        <Link
          to="/"
          className="text-sm text-muted-foreground hover:text-foreground hover:underline"
        >
          {t("pricing.back_home")}
        </Link>
      </header>

      <main className="relative z-10 mx-auto max-w-6xl px-6 pb-16 pt-4">
        <section className="mb-10 text-center">
          <h1 className="font-display text-4xl font-bold tracking-wide text-glow sm:text-5xl">
            {t("pricing.title")}
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
            {t("pricing.intro")}
          </p>
        </section>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {GOLD_PACKS.map((pack) => (
            <article
              key={pack.id}
              className="relative flex flex-col rounded-lg border border-border/60 bg-card/60 p-6 backdrop-blur-sm"
            >
              {pack.highlight && (
                <span className="absolute -top-3 right-4 rounded-full bg-primary px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-foreground">
                  {pack.highlight === "popular"
                    ? t("pricing.popular")
                    : t("pricing.best_value")}
                </span>
              )}
              <h2 className="font-display text-xl font-bold">{pack.label}</h2>
              <p className="mt-2 text-3xl font-bold text-foreground">
                {formatEuro(pack.priceCents)}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                {formatGold(pack.gold)} {t("pricing.gold_suffix")}
                {pack.bonusPct > 0 && (
                  <span className="ml-2 rounded bg-accent/20 px-2 py-0.5 text-xs font-medium text-accent">
                    {t("pricing.bonus", { pct: pack.bonusPct })}
                  </span>
                )}
              </p>
              <p className="mt-4 text-xs text-muted-foreground">
                {t("pricing.one_time")}
              </p>
            </article>
          ))}
        </div>

        <section className="mx-auto mt-12 max-w-3xl space-y-4 rounded-lg border border-border/40 bg-card/40 p-6 text-sm text-muted-foreground">
          <p>{t("pricing.tax_note")}</p>
          <p>
            {t("pricing.mor_note_prefix")}
            <strong className="text-foreground">{t("pricing.mor_note_seller")}</strong>
            {t("pricing.mor_note_suffix")}
          </p>
          <p>
            {t("pricing.refund_note_prefix")}
            <Link to="/widerruf" className="underline hover:text-foreground">
              {t("pricing.refund_link")}
            </Link>
            {t("pricing.refund_note_middle")}
            <Link to="/agb" className="underline hover:text-foreground">
              {t("pricing.terms_link")}
            </Link>
            {t("pricing.refund_note_suffix")}
          </p>
          <p>{t("pricing.account_note")}</p>
          <p className="text-xs italic opacity-80">{t("pricing.reviewer_note")}</p>
          <div className="pt-2">
            <Link
              to="/auth"
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90"
            >
              {t("pricing.login_cta")}
            </Link>
          </div>
        </section>
      </main>

      <LegalFooter />
    </div>
  );
}
